import React, { useState, useEffect } from 'react';
import './PlaidAccountsModal.css';
import { getPlaidAccounts, syncPlaidAccount, deletePlaidAccount } from '../api/plaid';

const PlaidAccountsModal = ({ isOpen, onClose, username, onAccountConnected }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [syncingAccount, setSyncingAccount] = useState(null);

  useEffect(() => {
    if (isOpen) fetchAccounts();
  }, [isOpen]);

  const fetchAccounts = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getPlaidAccounts();
      setAccounts(data);
    } catch { setError('Failed to load connected accounts'); }
    finally { setLoading(false); }
  };

  const syncAccount = async (accountId) => {
    setSyncingAccount(accountId);
    try {
      await syncPlaidAccount(accountId);
      fetchAccounts();
      if (onAccountConnected) onAccountConnected();
    } catch { setError('Failed to sync account'); }
    finally { setSyncingAccount(null); }
  };

  const deactivateAccount = async (accountId) => {
    if (!window.confirm('Are you sure you want to disconnect this account?')) return;
    try {
      await deletePlaidAccount(accountId);
      fetchAccounts();
    } catch { setError('Failed to disconnect account'); }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const getAccountTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'depository': return '🏦';
      case 'credit': return '💳';
      case 'loan': return '🏠';
      case 'investment': return '📈';
      default: return '💰';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="plaid-modal-overlay" onClick={onClose}>
      <div className="plaid-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="plaid-modal-header">
          <h2>Connected Bank Accounts</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="plaid-modal-body">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading connected accounts...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <div className="error-icon">⚠️</div>
              <p>{error}</p>
              <button className="retry-btn" onClick={fetchAccounts}>
                Try Again
              </button>
            </div>
          ) : accounts.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🏦</div>
              <h3>No Connected Accounts</h3>
              <p>You haven't connected any bank accounts yet. Use the "Connect Bank Account" option to get started.</p>
            </div>
          ) : (
            <div className="accounts-list">
              {accounts.map((account) => (
                <div key={account.id || account.plaidAccountId} className="account-card">
                  <div className="account-header">
                    <div className="account-info">
                      <div className="account-icon">
                        {getAccountTypeIcon(account.accountType)}
                      </div>
                      <div className="account-details">
                        <h4>{account.accountName || account.name}</h4>
                        <p className="institution">{account.institutionName || account.institution}</p>
                        <p className="account-meta">
                          {account.accountType} • {account.accountSubtype} • ****{account.mask}
                        </p>
                      </div>
                    </div>
                    <div className="account-balance">
                      <div className="balance-amount">
                        {formatCurrency(account.currentBalance || account.balance || 0)}
                      </div>
                      {account.availableBalance && (
                        <div className="available-balance">
                          Available: {formatCurrency(account.availableBalance)}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="account-actions">
                    <button 
                      className="action-btn sync-btn"
                      onClick={() => syncAccount(account.plaidAccountId || account.id)}
                      disabled={syncingAccount === (account.plaidAccountId || account.id)}
                    >
                      {syncingAccount === (account.plaidAccountId || account.id) ? (
                        <>
                          <div className="button-spinner"></div>
                          Syncing...
                        </>
                      ) : (
                        <>
                          <span>🔄</span>
                          Sync Account
                        </>
                      )}
                    </button>
                    <button 
                      className="action-btn disconnect-btn"
                      onClick={() => deactivateAccount(account.plaidAccountId || account.id)}
                    >
                      <span>🔌</span>
                      Disconnect
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="plaid-modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          <button className="btn btn-primary" onClick={fetchAccounts}>
            Refresh
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlaidAccountsModal;
